import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  type QueryKey,
  useSuspenseInfiniteQuery,
  type InfiniteData,
  type UseSuspenseInfiniteQueryResult,
} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {useCallback} from 'react'
import {parseLinkHeader} from '../../utils/parse-link-header'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {
  useGetThreadFromThreadID,
  useMarkersActions,
  useUnresolvedConversationCountActions,
} from '../../stores/selectors'
import {fileLevelCommentKey, type LineKey} from '@github-ui/diff-lines/types'
import {OUTSIDE_DIFF_KEY} from '../../helpers/diff-helpers'

export function threadsQueryKey(pathName: string): QueryKey {
  return [PageData.threads, pathName]
}

export type ThreadDataPage = {
  data: Thread[]
  nextUrl: string | null
  aliveChannel: string | null
}

type ThreadsResponse =
  | Thread[]
  | {
      threads: Thread[]
      aliveChannel: string
    }

type ThreadAliveEvent = {
  item_type?: string
  wait?: number
}

export function threadsApiUrl(pathName: string): string {
  return `${pathName}/page_data/${PageData.threads}`
}

export function unresolvedConversationCountDelta(existingThread: Thread, refreshedThread: Thread): -1 | 0 | 1 {
  if (existingThread.isResolved === false && refreshedThread.isResolved === true) {
    return -1
  }
  if (existingThread.isResolved === true && refreshedThread.isResolved === false) {
    return 1
  }
  return 0
}

/**
 * Hook for fetching full thread data with pagination support.
 * Returns complete thread data with all comments, unlike thread_previews which returns limited preview data.
 *
 * @param pathName - The base path for the API request
 * @param cursor - The cursor value for the next page (the minimum thread ID from the initial page)
 * @param hasMorePages - Whether there are more pages to fetch (based on whether the initial limit was exceeded)
 * @param initialThreads - Initial threads to use as the first page data (optional)
 * @returns The infinite query result with pagination controls
 */
export function useInfiniteThreadsPageData({
  pathName,
  cursor,
  hasMorePages = false,
  initialThreads = [],
}: {
  pathName: string
  cursor?: string
  hasMorePages?: boolean
  initialThreads?: Thread[]
}): UseSuspenseInfiniteQueryResult<InfiniteData<ThreadDataPage>> {
  const getThreadFromThreadID = useGetThreadFromThreadID()
  const {updateThread} = useMarkersActions()
  const {decrementUnresolvedConversationCount, incrementUnresolvedConversationCount} =
    useUnresolvedConversationCountActions()

  const reconcileThread = useCallback(
    (thread: Thread) => {
      const threadId = Number(thread.id)

      const existingThread = getThreadFromThreadID(threadId)

      if (existingThread) {
        const countDelta = unresolvedConversationCountDelta(existingThread, thread)
        if (countDelta === -1) {
          decrementUnresolvedConversationCount()
        } else if (countDelta === 1) {
          incrementUnresolvedConversationCount()
        }
      }

      const positioning = existingThread?.positioning
      if (!positioning) {
        updateThread(threadId, OUTSIDE_DIFF_KEY, OUTSIDE_DIFF_KEY, () => ({
          ...thread,
          shouldRenderInDiffLines: false,
        }))
        return
      }

      const isRightOrLeftSide = positioning.endDiffSide === 'RIGHT' ? 'R' : 'L'
      const isFileLevelComment = thread.subjectType?.toUpperCase() === 'FILE'
      const lineKey = isFileLevelComment
        ? fileLevelCommentKey
        : positioning.endLine
          ? (`${isRightOrLeftSide}${positioning.endLine}` as LineKey)
          : OUTSIDE_DIFF_KEY

      updateThread(threadId, positioning.path, lineKey, () => ({
        ...thread,
        positioning,
        // Preserve the thread's current visibility so a live update to an already
        // in-view thread isn't bounced through the hidden bucket
        hideUntilLoadedAndInView: existingThread?.hideUntilLoadedAndInView ?? false,
        shouldRenderInDiffLines: existingThread?.shouldRenderInDiffLines ?? true,
      }))
    },
    [decrementUnresolvedConversationCount, getThreadFromThreadID, incrementUnresolvedConversationCount, updateThread],
  )

  // Build the next page URL only if there are more pages to fetch.
  // When a cursor is provided, fetch pages after that cursor.
  // When no cursor is provided, fetch from the beginning (lazy-loading case).
  const nextPageUrl = hasMorePages
    ? cursor
      ? `${threadsApiUrl(pathName)}?after=${cursor}`
      : threadsApiUrl(pathName)
    : null

  const query = useSuspenseInfiniteQuery<ThreadDataPage, Error, InfiniteData<ThreadDataPage>, QueryKey, string>({
    queryKey: threadsQueryKey(pathName),
    initialPageParam: threadsApiUrl(pathName),
    initialData: {
      pages: [{data: initialThreads, nextUrl: nextPageUrl, aliveChannel: null}],
      pageParams: [threadsApiUrl(pathName)],
    },
    queryFn: async ({pageParam}: {pageParam: string}): Promise<ThreadDataPage> => {
      const result = await reactFetch(pageParam)
      if (!result.ok) throw new Error(`Failed to fetch thread data from ${pageParam}: HTTP ${result.status}`)

      const response = (await result.json()) as ThreadsResponse
      const {threads, aliveChannel} = Array.isArray(response) ? {threads: response, aliveChannel: null} : response
      const linkHeader = result.headers.get('Link')
      const nextUrl = parseLinkHeader(linkHeader, 'next')

      // Update threads in the store with full comment data
      for (const thread of threads) {
        reconcileThread(thread)
      }

      return {data: threads, nextUrl, aliveChannel}
    },
    getNextPageParam: (lastPage: ThreadDataPage) => {
      return lastPage.nextUrl ?? undefined
    },
    staleTime: Infinity,
    refetchOnMount: 'always',
    retry: 1, // Only retry once on failure to avoid endless loops
  })

  const aliveChannel = query.data.pages.find(page => page.aliveChannel)?.aliveChannel
  useInvalidateOnAliveUpdate<ThreadAliveEvent>(aliveChannel, threadsQueryKey(pathName), {
    filter: event => event.item_type === 'thread',
  })

  return query
}
